import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

import useLocationService from "../../api/locations/location.service";
import { Country, City } from "../../api/locations/location.responses";

import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/Card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/Tabs";
import { ScrollArea } from "../ui/ScrollArea";
import { Separator } from "../ui/Separator";
import { Combobox } from "../ui/Combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";

type DeleteTarget =
  | { type: "country"; country: Country }
  | { type: "city"; city: City }
  | null;

interface ManageLocationsCardProps {
  handleBackClick: () => void;
}

function ManageLocationsCard({ handleBackClick }: ManageLocationsCardProps) {
  const [countries, setCountries] = useState<Country[]>([]);
  const [cities, setCities] = useState<City[]>([]);

  const [newCountryName, setNewCountryName] = useState("");
  const [newCityName, setNewCityName] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);

  const {
    getCountries,
    createCountry,
    deleteCountry,
    getAllCitiesInCountry,
    createCity,
    deleteCityById,
  } = useLocationService();

  const fetchCountries = async () => {
    try {
      const res = await getCountries();
      setCountries(res.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Failed to load countries");
    }
  };

  const fetchCities = async (countryId: number) => {
    try {
      const res = await getAllCitiesInCountry(countryId);
      setCities(res.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Failed to load cities");
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      fetchCities(selectedCountry.id);
    } else {
      setCities([]);
    }
  }, [selectedCountry]);

  const handleAddCountry = async () => {
    const trimmed = newCountryName.trim();
    if (!trimmed) return;

    try {
      await createCountry({ name: trimmed });
      toast.success(`Country "${trimmed}" created`);
      setNewCountryName("");
      await fetchCountries();
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Failed to create country");
    }
  };

  const handleDeleteCountry = async (country: Country) => {
    try {
      await deleteCountry(country.id);
      toast.success(`Country "${country.name}" deleted`);
      if (selectedCountry?.id === country.id) {
        setSelectedCountry(null);
      }
      await fetchCountries();
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Failed to delete country");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === "country") {
      await handleDeleteCountry(deleteTarget.country);
    } else {
      await handleDeleteCity(deleteTarget.city);
    }
    setDeleteTarget(null);
  };

  const handleAddCity = async () => {
    const trimmed = newCityName.trim();
    if (!trimmed || !selectedCountry) return;

    try {
      await createCity(selectedCountry.id, { name: trimmed });
      toast.success(`City "${trimmed}" created`);
      setNewCityName("");
      await fetchCities(selectedCountry.id);
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Failed to create city");
    }
  };

  const handleDeleteCity = async (city: City) => {
    try {
      await deleteCityById(city.id);
      toast.success(`City "${city.name}" deleted`);
      if (selectedCountry) {
        await fetchCities(selectedCountry.id);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Failed to delete city");
    }
  };

  return (
    <Card className="w-[500px]">
      <CardHeader>
        <CardTitle>Manage Countries & Cities</CardTitle>
        <CardDescription>
          Add or remove countries and their cities.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="countries">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="countries">Countries</TabsTrigger>
            <TabsTrigger value="cities">Cities</TabsTrigger>
          </TabsList>

          <TabsContent value="countries" className="space-y-4 pt-2">
            <div className="flex gap-2">
              <Input
                placeholder="New country name"
                value={newCountryName}
                onChange={(e) => setNewCountryName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddCountry()}
              />
              <Button size="sm" onClick={handleAddCountry}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
            <Separator />
            <div className="relative">
              <ScrollArea className="h-[306px]">
                {countries.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No countries yet.
                  </p>
                ) : (
                  <div className="space-y-1 pb-6">
                    {countries.map((country) => (
                      <div
                        key={country.id}
                        className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-muted"
                      >
                        <span className="text-sm">{country.name}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setDeleteTarget({ type: "country", country })
                          }
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              {countries.length > 2 && (
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-card to-transparent" />
              )}
            </div>
          </TabsContent>

          <TabsContent value="cities" className="space-y-4 pt-2">
            <Combobox<Country>
              label="country"
              options={countries}
              selectedOption={selectedCountry}
              onOptionChange={setSelectedCountry}
              getOptionLabel={(c) => c.name}
              getOptionValue={(c) => c.id.toString()}
            />

            <div className="flex gap-2">
              <Input
                placeholder="New city name"
                value={newCityName}
                onChange={(e) => setNewCityName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddCity()}
                disabled={!selectedCountry}
              />
              <Button
                size="sm"
                onClick={handleAddCity}
                disabled={!selectedCountry}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
            <Separator />
            <div className="relative">
              <ScrollArea className="h-[250px]">
                {!selectedCountry ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Select a country first.
                  </p>
                ) : cities.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No cities in {selectedCountry.name}.
                  </p>
                ) : (
                  <div className="space-y-1 pb-6">
                    {cities.map((city) => (
                      <div
                        key={city.id}
                        className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-muted"
                      >
                        <span className="text-sm">{city.name}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setDeleteTarget({ type: "city", city })
                          }
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              {cities.length > 2 && (
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-card to-transparent" />
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter>
        <Button variant="outline" className="w-full" onClick={handleBackClick}>
          Back
        </Button>
      </CardFooter>

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              {deleteTarget?.type === "country"
                ? `This will delete the country "${deleteTarget.country.name}".`
                : deleteTarget?.type === "city"
                  ? `This will delete the city "${deleteTarget.city.name}".`
                  : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-between">
            <Button
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default ManageLocationsCard;
